import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import * as authApi from '../api/auth'
import type { User } from '../types'

interface AuthContextValue {
  user: User | null
  setUser: (user: User | null) => void
  loading: boolean
  isAuthenticated: boolean
  isStaff: boolean
  refresh: () => Promise<void>
  login: (username: string, password: string) => Promise<User>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function _autoTz(userData: User) {
  // Best-effort timezone sync — if the user's stored tz looks default,
  // detect the browser's IANA tz and sync it back.
  if (!userData.timezone || userData.timezone === 'America/New_York') {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (detected && detected !== userData.timezone) {
      authApi
        .updateProfile({ timezone: detected } as Partial<User>)
        .catch(() => {})
    }
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      setUser(null)
      setLoading(false)
      return
    }
    try {
      const userData = await authApi.me()
      setUser(userData)
      _autoTz(userData)
    } catch {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    await authApi.login(username, password)
    const userData = await authApi.me()
    setUser(userData)
    _autoTz(userData)
    return userData
  }, [])

  const logout = useCallback(async () => {
    await authApi.logout()
    setUser(null)
    // Reset cached Stripe.js promise so the next user gets a fresh load
    // (mode may differ if they're a test-mode user).
    const { resetStripeSingleton } = await import('../lib/stripe')
    resetStripeSingleton()
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const value: AuthContextValue = {
    user,
    setUser,
    loading,
    isAuthenticated: user !== null,
    isStaff: !!user?.is_staff,
    refresh,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}
